import { HttpBackend, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, inject, Injectable, NgZone } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MessageService } from '@openng/optimus-ui/api';
import { catchError, EMPTY, filter, Subject, switchMap, tap, TimeoutError, timeout } from 'rxjs';
import { API_CONFIG } from '../tokens/config.tokens';

export const CONNECTIVITY_TOAST_KEY = 'connectivity';

const OFFLINE_POLL_START_MS = 5_000;
const OFFLINE_POLL_MAX_MS = 30_000;
const PROBE_TIMEOUT_MS = 4_000;

type ConnectivityIssue = 'offline' | 'unreachable';

/**
 * App-wide connectivity. Health probes run only while unreachable — zero cost when online.
 * Interceptor calls setOffline/setOnline for instant feedback on real API traffic.
 *
 * HTTP status 0 is ambiguous (no network vs server down). We use navigator.onLine to
 * tell those apart so a down API is not reported as "no internet".
 */
@Injectable({ providedIn: 'root' })
export class ConnectivityService {
  readonly #apiConfig = inject(API_CONFIG);
  readonly #http = new HttpClient(inject(HttpBackend));
  readonly #messages = inject(MessageService);
  readonly #zone = inject(NgZone);
  readonly #destroyRef = inject(DestroyRef);

  readonly #probe$ = new Subject<void>();

  #issue: ConnectivityIssue | null = null;
  #offlineDelay = OFFLINE_POLL_START_MS;
  #timer: ReturnType<typeof setTimeout> | null = null;
  #started = false;

  readonly #onBrowserOffline = (): void => this.#setIssue('offline');
  readonly #onBrowserOnline = (): void => {
    // Only probe if we already showed an issue — no /Health traffic in the normal case.
    if (this.#issue) this.probe();
  };

  start(): void {
    if (this.#started) return;
    this.#started = true;

    this.#probe$
      .pipe(
        filter(() => this.#issue !== null),
        switchMap(() => {
          this.clearTimer();
          return this.#http.get(`${this.#apiConfig.baseUrlV2}/Health`, { params: { _: Date.now().toString() } }).pipe(
            timeout(PROBE_TIMEOUT_MS),
            tap(() => this.setOnline()),
            catchError((err: unknown) => {
              // Server answered (4xx/5xx) ⇒ reachable. Only status 0 / timeout ⇒ still down.
              if (isUnreachableError(err)) {
                if (this.#issue) {
                  this.#setIssue(navigator.onLine ? 'unreachable' : 'offline');
                  this.scheduleNext();
                }
              } else {
                this.setOnline();
              }
              return EMPTY;
            })
          );
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();

    window.addEventListener('offline', this.#onBrowserOffline);
    window.addEventListener('online', this.#onBrowserOnline);

    this.#destroyRef.onDestroy(() => {
      window.removeEventListener('offline', this.#onBrowserOffline);
      window.removeEventListener('online', this.#onBrowserOnline);
      this.clearTimer();
      this.#probe$.complete();
    });

    if (!navigator.onLine) this.#setIssue('offline');
  }

  /** Status 0 from API traffic. Browser online ⇒ server down; browser offline ⇒ no internet. */
  setOffline(): void {
    this.#setIssue(navigator.onLine ? 'unreachable' : 'offline');
  }

  setOnline(): void {
    if (this.#issue === null) return;
    const previous = this.#issue;
    this.#issue = null;
    this.clearTimer();
    this.#offlineDelay = OFFLINE_POLL_START_MS;
    this.showRecoveredToast(previous);
  }

  /** Only meaningful while down — no-ops if already online. Latest call cancels any in-flight probe. */
  probe(): void {
    if (!this.#issue) return;
    this.#probe$.next();
  }

  scheduleNext(): void {
    this.clearTimer();
    if (!this.#issue) return;

    const delay = this.#offlineDelay;
    this.#offlineDelay = Math.min(this.#offlineDelay * 2, OFFLINE_POLL_MAX_MS);
    this.#timer = setTimeout(() => this.probe(), delay);
  }

  clearTimer(): void {
    if (this.#timer) clearTimeout(this.#timer);
    this.#timer = null;
  }

  #setIssue(issue: ConnectivityIssue): void {
    if (this.#issue === issue) return;
    const alreadyDown = this.#issue !== null;
    this.#issue = issue;
    this.showIssueToast();
    if (!alreadyDown) {
      this.#offlineDelay = OFFLINE_POLL_START_MS;
      this.scheduleNext();
    }
  }

  showIssueToast(): void {
    const summary =
      this.#issue === 'offline'
        ? 'No internet connection. Please check your network.'
        : 'Unable to reach the server. Please try again later.';

    this.#zone.run(() => {
      this.#messages.clear(CONNECTIVITY_TOAST_KEY);
      this.#messages.add({
        key: CONNECTIVITY_TOAST_KEY,
        severity: 'error',
        summary,
        sticky: true,
        closable: false
      });
    });
  }

  showRecoveredToast(previous: ConnectivityIssue): void {
    const summary = previous === 'offline' ? "You're back online." : 'Server connection restored.';

    this.#zone.run(() => {
      this.#messages.clear(CONNECTIVITY_TOAST_KEY);
      this.#messages.add({
        key: CONNECTIVITY_TOAST_KEY,
        severity: 'success',
        summary,
        life: 3_000,
        closable: false
      });
    });
  }
}

function isUnreachableError(err: unknown): boolean {
  if (err instanceof TimeoutError) return true;
  if (err instanceof HttpErrorResponse) return err.status === 0;
  return false;
}
